import { ChangeEvent, type FC, useCallback } from 'react';
import { Icon } from '@iconify/react';
import {
  Box,
  Button,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useSettingsContext } from 'src/hooks/use-context';
import { hasFeatureFlags } from 'src/libs/feature-flags';
import { paths } from 'src/paths';
import { icons } from 'src/theme/icons';
import { Profile } from 'src/types/user';
import { formatFullName } from 'src/utils/format';

interface AccountPopoverProps {
  anchorEl: null | Element;
  profile: Profile;
  onLogout?: () => void;
  onSwitch?: () => void;
  onColorMode?: () => void;
  onClose?: () => void;
  open?: boolean;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const {
    anchorEl,
    profile,
    onLogout,
    onSwitch,
    onColorMode,
    onClose,
    open,
    ...other
  } = props;

  const [hasDarkMode] = hasFeatureFlags('dark-mode');
  const { settings, changeSetting } = useSettingsContext();

  const handleDarkMode = useCallback(
    (_: ChangeEvent, checked: boolean) =>
      changeSetting('colorMode', checked ? 'dark' : 'light'),
    [changeSetting]
  );

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom'
      }}
      disableScrollLock
      onClose={onClose}
      open={!!open}
      PaperProps={{ sx: { width: 300 } }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">{formatFullName(profile)}</Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          data-testid="account-popover-email"
        >
          {profile?.email}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 1 }}>
        <ListItemButton
          component={Link}
          to={paths.company.user}
          onClick={onClose}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}
        >
          <ListItemIcon>
            <Icon icon="fa6-solid:building" />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body1">Mein Betrieb</Typography>}
          />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={paths.account}
          onClick={onClose}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}
        >
          <ListItemIcon>
            <Icon icon={icons.profile} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body1">Mein Profil</Typography>}
          />
        </ListItemButton>
      </Box>
      {hasDarkMode && (
        <>
          <Divider sx={{ my: '0 !important' }} />
          <Box sx={{ p: 1 }}>
            <ListItem
              sx={{
                borderRadius: 1,
                px: 1,
                py: 0.5
              }}
            >
              <ListItemIcon>
                <Icon icon="fa6-solid:moon" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1">Dunkle Ansicht</Typography>
                }
              />
              <Switch
                size="small"
                edge="end"
                onChange={handleDarkMode}
                checked={settings.colorMode === 'dark'}
                inputProps={{
                  'aria-labelledby': 'switch-list-label-wifi'
                }}
              />
            </ListItem>
          </Box>
        </>
      )}
      <Divider sx={{ my: '0 !important' }} />
      <Stack
        direction="column"
        sx={{
          display: 'flex',
          p: 1,
          justifyContent: 'center'
        }}
      >
        {onLogout && (
          <Button
            color="inherit"
            onClick={onLogout}
            size="small"
            data-testid="account-popover-logout"
          >
            Logout
          </Button>
        )}
        {onSwitch && (
          <Button
            color="inherit"
            onClick={onSwitch}
            size="small"
          >
            Account wechseln
          </Button>
        )}
      </Stack>
    </Popover>
  );
};
